import { createContext, useContext, useEffect, useState } from 'react';

/**
 * RelativeTimeProvider — a shared "now" clock for relative-time displays (#242).
 *
 * `AlarmRow` (and any other relative-time leaf) renders strings like "5m ago"
 * from a `now` reference. Computed once at render, that string goes stale: a row
 * mounted once and never re-rendered shows "5m ago" forever. In flows that
 * refetch on a timer (e.g. `useCustomerAlarms` every 10s) the parent re-renders
 * often enough to hide this, but sparse surfaces — the bell popover that opens
 * once and doesn't poll — never refresh.
 *
 * A consumer wraps the relevant subtree in `<RelativeTimeProvider>`; the provider
 * ticks `Date.now()` on an interval (default 60s) and consumers read it via
 * `useRelativeNow()`, driving a re-render each tick. Without a provider the hook
 * returns a fresh render-time `Date.now()` (current behavior — leaves unchanged).
 *
 * The context default is `null` (not `Date.now()`): a literal-number default
 * would be evaluated once at module load and then frozen, so a provider-less
 * consumer would compute relative times against app-start time forever. The null
 * sentinel lets `useRelativeNow()` substitute a render-time `Date.now()` instead.
 */
const RelativeTimeContext = createContext<number | null>(null);

export interface RelativeTimeProviderProps {
  /** Tick interval in ms. Default 60_000 (minute granularity). */
  tickMs?: number;
  children: React.ReactNode;
}

export function RelativeTimeProvider({
  tickMs = 60_000,
  children,
}: RelativeTimeProviderProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [tickMs]);

  return (
    <RelativeTimeContext.Provider value={now}>
      {children}
    </RelativeTimeContext.Provider>
  );
}

/**
 * Current "now" in ms. Inside a `<RelativeTimeProvider>` this updates on each
 * tick and re-renders the consumer; outside one it falls back to a fresh
 * render-time `Date.now()` (the null context default is substituted per call).
 */
export function useRelativeNow(): number {
  return useContext(RelativeTimeContext) ?? Date.now();
}
